import { useEffect, useState } from "react";
import { api, errMsg, inr } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Loader2, CalendarDays } from "lucide-react";

const bal = (v) => (typeof v === "number" ? inr(v) : "—");

const Card = ({ title, children, testid, aside }) => (
  <div className="rounded-lg border border-border bg-white" data-testid={testid}>
    <div className="flex items-center justify-between border-b border-border px-5 py-3">
      <h3 className="flex items-center gap-2 font-display text-base font-semibold">
        <CalendarDays className="h-4 w-4 text-primary" /> {title}
      </h3>
      {aside}
    </div>
    <div className="p-5">{children}</div>
  </div>
);

const Metric = ({ label, value, note, testid }) => (
  <div className="rounded-md border border-border p-4" data-testid={testid}>
    <p className="text-xs uppercase tracking-[0.15em] text-muted-foreground">{label}</p>
    <p className="num mt-1 font-display text-lg font-bold tracking-tight">{value}</p>
    {note && <p className="mt-1 text-[11px] text-muted-foreground">{note}</p>}
  </div>
);

const sourceClass = (src) =>
  src === "Transaction day"
    ? "bg-ok text-ok-foreground border-green-200"
    : src === "Opening balance"
      ? "bg-secondary text-secondary-foreground"
      : "bg-muted text-muted-foreground";

export const EodBalancePanel = ({ jobId }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let alive = true;
    setData(null);
    setError(null);
    api.get(`/analysis/${jobId}/cam`, { params: { section: "daily_balance" } })
      .then(({ data }) => { if (alive) setData(data.data); })
      .catch((e) => { if (alive) setError(errMsg(e)); });
    return () => { alive = false; };
  }, [jobId]);

  if (error)
    return (
      <Card title="EOD Balance (Date-wise)" testid="eod-panel-error">
        <p className="text-sm text-muted-foreground" data-testid="eod-error">{error}</p>
      </Card>
    );

  if (!data)
    return (
      <Card title="EOD Balance (Date-wise)" testid="eod-panel-loading">
        <div className="flex h-24 items-center justify-center">
          <Loader2 className="h-5 w-5 animate-spin text-primary" />
        </div>
      </Card>
    );

  const series = data.eod_series || [];
  const summ = data.eod_summary || {};
  const w6 = summ.dab_last_6_months || {};
  const w12 = summ.dab_last_12_months || {};
  const full = summ.dab_full_period || {};
  const dateAvgs = summ.date_specific_averages || [];

  if (series.length === 0)
    return (
      <Card title="EOD Balance (Date-wise)" testid="eod-panel-empty">
        <p className="text-sm text-muted-foreground" data-testid="eod-empty">
          No dated balances are available in this statement, so an EOD balance table could not be built.
        </p>
      </Card>
    );

  return (
    <Card title="EOD Balance (Date-wise)" testid="eod-balance-panel"
          aside={<Badge variant="outline" className="bg-secondary text-secondary-foreground">
            {summ.calendar_days_in_period} calendar day(s)</Badge>}>
      <div className="space-y-5">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
            Daily Average Balance (DAB)
          </p>
          <div className="mt-3 grid gap-3 sm:grid-cols-3" data-testid="dab-summary">
            <Metric label="DAB — last 6 months" value={bal(w6.daily_average_balance)}
                    note={`${w6.months_used ?? 0} month(s), ${w6.days_counted ?? 0} day(s)${w6.note ? " — " + w6.note : ""}`}
                    testid="dab-6m" />
            <Metric label="DAB — last 12 months" value={bal(w12.daily_average_balance)}
                    note={`${w12.months_used ?? 0} month(s), ${w12.days_counted ?? 0} day(s)${w12.note ? " — " + w12.note : ""}`}
                    testid="dab-12m" />
            <Metric label="DAB — full period" value={bal(full.daily_average_balance)}
                    note={`${full.days_counted ?? 0} day(s)`} testid="dab-full" />
          </div>
        </div>

        <div className="grid gap-3 rounded-md bg-muted p-4 text-xs sm:grid-cols-4" data-testid="eod-period-summary">
          <p><span className="text-muted-foreground">Period:</span> <b>{summ.period_start} – {summ.period_end}</b></p>
          <p><span className="text-muted-foreground">Basis:</span> <b>{summ.period_basis}</b></p>
          <p><span className="text-muted-foreground">Transaction days:</span> <b className="num">{summ.transaction_days}</b></p>
          <p><span className="text-muted-foreground">Carried forward / opening:</span> <b className="num">{summ.carried_forward_days}</b></p>
        </div>

        {dateAvgs.length > 0 && (
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Average balance on specific days of the month
            </p>
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-sm" data-testid="date-specific-averages">
                <thead><tr className="border-b border-border text-xs uppercase text-muted-foreground">
                  <th className="py-2 text-left">Day of month</th>
                  <th className="text-right">Average Closing Balance</th>
                  <th className="text-right">Occurrences</th>
                </tr></thead>
                <tbody>
                  {dateAvgs.map((d) => (
                    <tr key={d.day_of_month} className="border-b border-border/60">
                      <td className="py-2">Day {d.day_of_month}</td>
                      <td className="num text-right">{bal(d.average_closing_balance)}</td>
                      <td className="num text-right">{d.occurrences}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <div>
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
              Date-wise closing balance
            </p>
            <span className="text-xs text-muted-foreground">{series.length} row(s)</span>
          </div>
          <div className="max-h-[420px] overflow-auto rounded-md border border-border" data-testid="eod-table-scroll">
            <table className="w-full text-sm">
              <thead className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_var(--border)]">
                <tr className="text-xs uppercase text-muted-foreground">
                  <th className="px-3 py-2 text-left">Date</th>
                  <th className="px-3 text-right">Closing Balance</th>
                  <th className="px-3 text-right">Credits</th>
                  <th className="px-3 text-right">Debits</th>
                  <th className="px-3 text-right">Txns</th>
                  <th className="px-3 text-right">Source</th>
                </tr>
              </thead>
              <tbody data-testid="eod-table-body">
                {series.map((s) => (
                  <tr key={s.date} className="border-t border-border/60">
                    <td className="px-3 py-1.5 tabular-nums">{s.date}</td>
                    <td className="num px-3 text-right font-medium">{bal(s.closing_balance)}</td>
                    <td className="num px-3 text-right text-green-700">{s.credits ? inr(s.credits) : "—"}</td>
                    <td className="num px-3 text-right text-red-700">{s.debits ? inr(s.debits) : "—"}</td>
                    <td className="num px-3 text-right">{s.transaction_count || "—"}</td>
                    <td className="px-3 text-right">
                      <Badge variant="outline" className={`text-[10px] ${sourceClass(s.source)}`}>{s.source}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="mt-2 text-[11px] text-muted-foreground">
            Days with no transaction carry the previous day's closing balance forward; days before the first
            transaction show the statement opening balance.
          </p>
        </div>
      </div>
    </Card>
  );
};
